import type {
  ApplicationFieldConfig,
  ApplicationStep,
  ApplicationStructure,
} from '../types/audition.types';

/**
 * Step (group) normalization for the application form structure.
 *
 * The structure has two shapes that must both be supported:
 *  - **Step model** (`structure.groups`) — the source of truth when present.
 *  - **Legacy flat** (`structure.fields` only) — older structures with a flat
 *    field list where a `heading` field acts as a soft section divider.
 *
 * `toApplicationGroups` collapses either shape into ordered steps so the
 * builder + renderer can work against one representation. Mirrors the Meteor
 * application-process group model (see the parity map, §1/§11).
 */

const DEFAULT_STEP_KEY = 'application';
const DEFAULT_STEP_NAME = 'Application';

/**
 * Normalize an application structure into ordered steps.
 *
 * - If `groups` is present and non-empty, returns it sorted by `sortOrder`.
 * - Otherwise builds steps from the flat `fields`, starting a new step at each
 *   `heading` field (its label becomes the step name; the heading field itself
 *   is consumed into the step title and dropped). Fields before the first
 *   heading land in a default "Application" step.
 */
export function toApplicationGroups(
  structure: ApplicationStructure,
): ApplicationStep[] {
  if (structure.groups && structure.groups.length > 0) {
    return [...structure.groups].sort((a, b) => a.sortOrder - b.sortOrder);
  }

  const sorted = [...(structure.fields ?? [])].sort(
    (a, b) => a.sortOrder - b.sortOrder,
  );

  const groups: ApplicationStep[] = [];
  let current: ApplicationStep | null = null;
  let order = 0;

  for (const field of sorted) {
    if (field.type === 'heading') {
      if (current) groups.push(current);
      current = {
        key: field.key,
        name: field.label || 'Section',
        fields: [],
        sortOrder: order++,
      };
      continue;
    }

    if (!current) {
      current = {
        key: DEFAULT_STEP_KEY,
        name: DEFAULT_STEP_NAME,
        fields: [],
        sortOrder: order++,
      };
    }
    current.fields.push(field);
  }

  if (current) groups.push(current);

  if (groups.length === 0) {
    groups.push({
      key: DEFAULT_STEP_KEY,
      name: DEFAULT_STEP_NAME,
      fields: [],
      sortOrder: 0,
    });
  }

  return groups;
}

/**
 * Flatten ordered steps back into a single field list (re-sequencing
 * `sortOrder` across all fields). Used when a flat representation is needed
 * (legacy readers, the `fields` mirror on the structure).
 */
export function flattenApplicationGroups(
  groups: ApplicationStep[],
): ApplicationFieldConfig[] {
  const ordered = [...groups].sort((a, b) => a.sortOrder - b.sortOrder);
  const out: ApplicationFieldConfig[] = [];
  let order = 0;
  for (const group of ordered) {
    for (const field of group.fields) {
      out.push({ ...field, sortOrder: order++ });
    }
  }
  return out;
}

/**
 * Ensure every field key in the structure is unique across all steps/fields.
 * Collisions get a numeric suffix (`key`, `key_1`, `key_2`, …), mirroring the
 * Meteor `ensureUniqueKeys` behavior. Operates on `groups` when present,
 * otherwise on the flat `fields`. Returns a new structure; never mutates input.
 */
export function ensureUniqueFieldKeys(
  structure: ApplicationStructure,
): ApplicationStructure {
  const seen = new Set<string>();

  const dedupe = (key: string): string => {
    if (!seen.has(key)) {
      seen.add(key);
      return key;
    }
    let suffix = 1;
    let candidate = `${key}_${suffix}`;
    while (seen.has(candidate)) {
      suffix += 1;
      candidate = `${key}_${suffix}`;
    }
    seen.add(candidate);
    return candidate;
  };

  // Dedupe across BOTH representations with one shared `seen` set so a
  // structure that carries both `groups` and a flat `fields` mirror can't end
  // up with one deduped and the other stale (review M2). `groups` is processed
  // first when present (it's the source of truth for the stepper).
  const hasGroups = !!structure.groups && structure.groups.length > 0;

  const groups = hasGroups
    ? structure.groups!.map((group) => ({
        ...group,
        fields: group.fields.map((field) => ({
          ...field,
          key: dedupe(field.key),
        })),
      }))
    : undefined;

  const fields = (structure.fields ?? []).map((field) => ({
    ...field,
    key: dedupe(field.key),
  }));

  return {
    ...structure,
    ...(groups ? { groups } : {}),
    fields,
  };
}

/**
 * True when `value` is a usable ApplicationStructure for the structure-driven
 * renderer: a non-null object exposing a `fields` array or a `groups` array.
 *
 * Guards against non-null-but-foreign blobs that the renderer can't parse —
 * notably the legacy Meteor competition shape `{ sections: [...] }` (id-based,
 * no `fields`/`groups`). Callers resolving `audition.application_structure`
 * MUST fall back to the canonical default when this returns false; otherwise a
 * `?? default` check passes the garbage through and the renderer shows a
 * 0-field form. A data migration backfills known offenders, but this is the
 * defense-in-depth so a future foreign write can't silently break the apply.
 */
export function isValidApplicationStructure(
  value: unknown,
): value is ApplicationStructure {
  if (!value || typeof value !== 'object') return false;
  const s = value as Record<string, unknown>;
  return Array.isArray(s.fields) || Array.isArray(s.groups);
}
